import * as SingleSpa from 'single-spa'
import {
  createContext,
  initializeContext,
  FeatureFlag,
  RoutePaths,
} from '@tangocard/portal-app-context'
import {
  constructApplications,
  constructLayoutEngine,
  constructRoutes,
} from 'single-spa-layout'
import { createLayout } from './layout'
import './main.css'
import config from './config'
import TagManager from 'react-gtm-module'
import './vwo-integration'
import './stale-chunk-refresh-notification'

if (import.meta.env.VITE_GTM_ID) {
  TagManager.initialize({
    gtmId: import.meta.env.VITE_GTM_ID,
  })
}

/**
 * Builds a redirect URL that preserves query params from both
 * before the hash (e.g., ?utm_source=foo#/signup) and
 * after the hash (e.g., /vue/#/signup?utm_source=foo).
 */
function buildRedirectUrl(targetPath: string): string {
  const preHashParams = new URLSearchParams(window.location.search)

  const hashQueryIndex = window.location.hash.indexOf('?')
  const postHashParams =
    hashQueryIndex !== -1
      ? new URLSearchParams(window.location.hash.substring(hashQueryIndex))
      : new URLSearchParams()

  const mergedParams = new URLSearchParams([
    ...postHashParams.entries(),
    ...preHashParams.entries(),
  ])

  const queryString = mergedParams.toString()
  return queryString ? `${targetPath}?${queryString}` : targetPath
}

export const initializeApp = async () => {
  const context = await createContext(config).then((context) =>
    initializeContext(context),
  )

  // Query params may appear in the legacy route before the hash
  const isOnLegacySignupPage =
    window.location.href.includes(RoutePaths.RG_SIGNUP) ||
    window.location.hash.startsWith('#/signup')

  // Check if we need to redirect legacy signup before setting up routes
  const shouldRedirectToNewSignup =
    context.featureFlagClient?.isFeatureFlagEnabled(
      FeatureFlag.SimpleSignupRedirect,
    ) && isOnLegacySignupPage

  if (shouldRedirectToNewSignup) {
    window.location.replace(buildRedirectUrl(RoutePaths.GET_STARTED))
    return
  }

  // Configure single spa applications
  const routesConfig = createLayout(context)
  const routes = constructRoutes(routesConfig)

  const applications = constructApplications({
    routes,
    loadApp: ({ name }) =>
      import(/* @vite-ignore */ name) as Promise<SingleSpa.LifeCycles>,
  })

  applications.forEach((app) => {
    SingleSpa.registerApplication({
      ...app,
      customProps: {
        ...(app.customProps ?? {}),
        context, // inject app context into all apps (feature flags, monitoring, etc.)
      },
    })
  })

  // Define layout
  const layoutEngine = constructLayoutEngine({ routes, applications })
  layoutEngine.activate()

  // Start application
  SingleSpa.start()

  // Initialize cookie consent
  if (context.featureFlagClient?.isFeatureFlagEnabled(FeatureFlag.TrustArc)) {
    await context.cookieConsentClient?.init()
  }

  // Monitor app loading state
  if (context.authService.isAuthenticated()) {
    document.body.classList.add('loading')
    const startTime = Date.now()
    const maxWaitTime = 10000 // 10 seconds in milliseconds
    const checkApps = () => {
      const appsWithoutContent = Array.from(
        document.querySelectorAll('[id^="single-spa-application:"]'),
      ).filter((el) => !el.innerHTML)

      if (
        appsWithoutContent.length === 0 ||
        Date.now() - startTime >= maxWaitTime
      ) {
        setTimeout(() => document.body.classList.remove('loading'), 1250)
      } else {
        setTimeout(checkApps, 250)
      }
    }

    checkApps()
  }
}

initializeApp().then()
